<template>
  <v-form ref="form" @submit.prevent="submit" data-cy="guidedRecording-organizer-form">
    <v-alert type="info" color="primary" variant="tonal" density="comfortable" class="mb-5">
      {{ $t('routes.guidedRecording.formDescription') }}
    </v-alert>

    <v-alert
      v-if="formError"
      type="error"
      variant="tonal"
      class="mb-5"
      role="alert"
      aria-live="assertive"
      data-cy="guidedRecording-organizer-formError"
    >
      {{ formError }}
    </v-alert>

    <p class="sr-only" role="status" aria-live="polite" aria-atomic="true" data-cy="guidedRecording-question-announcement">
      {{ announcement }}
    </p>

    <div class="mb-5">
      <span id="guided-recording-destination-label" class="text-body-1 d-block mb-2">
        {{ $t('routes.guidedRecording.destination') }}
      </span>
      <SelectNodeDialog
        :model-value="modelValue.destinationNodeId"
        :is-disabled-selecting-node="disabled"
        @update:model-value="updateDestination"
      >
        <template v-slot:default="slotProps">
          <v-btn
            ref="destinationButton"
            block
            variant="outlined"
            color="primary"
            :disabled="disabled"
            :aria-labelledby="'guided-recording-destination-label guided-recording-destination-value'"
            data-cy="guidedRecording-organizer-destination"
          >
            <span id="guided-recording-destination-value">
              {{ slotProps.selectedNode ? slotProps.selectedNode.name : $t('routes.guidedRecording.selectDestination') }}
            </span>
          </v-btn>
        </template>
      </SelectNodeDialog>
      <v-alert
        v-if="displayDestinationError"
        type="error"
        variant="tonal"
        density="compact"
        class="mt-2"
        role="alert"
        aria-live="assertive"
        data-cy="guidedRecording-organizer-destinationError"
      >
        {{ displayDestinationError }}
      </v-alert>
    </div>

    <v-text-field
      :model-value="modelValue.title"
      :label="$t('routes.guidedRecording.title')"
      :rules="requiredRules"
      :disabled="disabled"
      :error-messages="fieldErrors.title"
      variant="solo-filled"
      required
      counter="255"
      maxlength="255"
      data-cy="guidedRecording-organizer-title"
      @update:model-value="updateField('title', $event)"
    />

    <v-textarea
      :model-value="modelValue.introduction"
      :label="$t('routes.guidedRecording.introduction')"
      :hint="$t('routes.guidedRecording.optional')"
      :disabled="disabled"
      :error-messages="fieldErrors.introduction"
      variant="solo-filled"
      rows="3"
      auto-grow
      counter="65535"
      maxlength="65535"
      data-cy="guidedRecording-organizer-introduction"
      @update:model-value="updateField('introduction', $event)"
    />

    <v-textarea
      :model-value="modelValue.instructions"
      :label="$t('routes.guidedRecording.instructions')"
      :hint="$t('routes.guidedRecording.optional')"
      :disabled="disabled"
      :rules="instructionsRules"
      :error-messages="fieldErrors.instructions"
      variant="solo-filled"
      rows="4"
      auto-grow
      :counter="maximumInstructionsLength"
      :maxlength="maximumInstructionsLength"
      persistent-counter
      data-cy="guidedRecording-organizer-instructions"
      @update:model-value="updateField('instructions', $event)"
    />

    <v-switch
      :model-value="modelValue.active === true"
      :label="$t('routes.guidedRecording.active')"
      :disabled="disabled"
      color="primary"
      data-cy="guidedRecording-organizer-active"
      @update:model-value="updateField('active', $event)"
    />

    <section class="guided-recording-consent mb-5" aria-labelledby="guided-recording-consent-heading">
      <h2 id="guided-recording-consent-heading" class="text-subtitle-1 mb-2">
        {{ $t('routes.guidedRecording.organizerConsent') }}
      </h2>
      <v-switch
        :model-value="modelValue.consentRequired === true"
        :label="$t('routes.guidedRecording.organizerConsentRequired')"
        :hint="$t('routes.guidedRecording.organizerConsentStatement')"
        :disabled="disabled"
        color="primary"
        persistent-hint
        data-cy="guidedRecording-organizer-consentRequired"
        @update:model-value="updateField('consentRequired', $event)"
      />
    </section>

    <section class="guided-recording-audio-analysis mb-5" aria-labelledby="guided-recording-audio-analysis-heading">
      <h2 id="guided-recording-audio-analysis-heading" class="text-subtitle-1 mb-2">
        {{ $t('routes.guidedRecording.organizerAudioAnalysis') }}
      </h2>
      <v-switch
        :model-value="modelValue.audioAnalysisEnabled === true"
        :label="$t('routes.guidedRecording.organizerAudioAnalysisEnabled')"
        :hint="$t('routes.guidedRecording.organizerAudioAnalysisHint')"
        :aria-label="$t('routes.guidedRecording.organizerAudioAnalysisAriaLabel')"
        :disabled="disabled"
        color="primary"
        persistent-hint
        data-cy="guidedRecording-organizer-audioAnalysisEnabled"
        @update:model-value="updateField('audioAnalysisEnabled', $event)"
      />
    </section>

    <section class="guided-recording-expiry mb-5" aria-labelledby="guided-recording-expiry-heading">
      <h2 id="guided-recording-expiry-heading" class="text-subtitle-1 mb-2">
        {{ $t('routes.guidedRecording.expiry') }}
      </h2>
      <v-date-input
        :model-value="modelValue.expiresAt"
        :label="$t('routes.guidedRecording.expiryDate')"
        :hint="$t('routes.guidedRecording.expiryHint')"
        :rules="expiryRules"
        :error-messages="fieldErrors.expiresAt"
        :disabled="disabled"
        :aria-label="$t('routes.guidedRecording.expiryDate')"
        show-adjacent-months
        rounded="md"
        clearable
        persistent-hint
        append-inner-icon="mdi-calendar"
        prepend-icon=""
        variant="solo-filled"
        data-cy="guidedRecording-organizer-expiry"
        @update:model-value="updateField('expiresAt', toDateOnly($event))"
      />
      <p
        class="text-body-2 mt-2 mb-0"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-cy="guidedRecording-organizer-expiryStatus"
      >
        {{ expiryStatus }}
      </p>
    </section>

    <section class="guided-recording-questions mt-6" aria-labelledby="questionsHeadingId">
      <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-4">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2" aria-hidden="true">mdi-message-question-outline</v-icon>
          <h2 :id="questionsHeadingId" class="text-subtitle-1">{{ $t('routes.guidedRecording.questions') }}</h2>
        </div>
      </div>

      <v-alert
        v-if="questionError"
        type="error"
        variant="tonal"
        class="mb-4"
        role="alert"
        data-cy="guidedRecording-organizer-questionError"
      >
        {{ questionError }}
      </v-alert>

      <ol class="guided-recording-question-list pa-0 ma-0" data-cy="guidedRecording-organizer-questionList">
        <li v-for="(question, index) in displayQuestions" :key="questionKey(question, index)" class="guided-recording-question-card mb-4">
          <v-card
            variant="outlined"
            class="pa-4"
            :data-cy="hasPersistedQuestions ? undefined : 'guidedRecording-organizer-initialQuestion'"
          >
            <div class="d-flex flex-wrap align-center justify-space-between ga-2 mb-3">
              <h3
                :ref="element => setQuestionRef(questionKey(question, index), element)"
                class="text-subtitle-1"
                tabindex="-1"
              >
                {{ $t('routes.guidedRecording.questionNumber', { number: index + 1 }) }}
              </h3>
              <div class="d-flex flex-wrap align-center ga-1" v-if="hasPersistedQuestions">
                <v-btn
                  icon="mdi-arrow-up"
                  variant="text"
                  :aria-label="$t('routes.guidedRecording.moveQuestionUp', { number: index + 1 })"
                  :disabled="disabled || mutatingQuestions || index === 0"
                  data-cy="guidedRecording-organizer-moveQuestionUp"
                  @click="requestReorder(question, index, 'up')"
                />
                <v-btn
                  icon="mdi-arrow-down"
                  variant="text"
                  :aria-label="$t('routes.guidedRecording.moveQuestionDown', { number: index + 1 })"
                  :disabled="disabled || mutatingQuestions || index === questionDrafts.length - 1"
                  data-cy="guidedRecording-organizer-moveQuestionDown"
                  @click="requestReorder(question, index, 'down')"
                />
                <v-btn
                  icon="mdi-delete-outline"
                  variant="text"
                  color="error"
                  :aria-label="$t('routes.guidedRecording.removeQuestion', { number: index + 1 })"
                  :disabled="disabled || mutatingQuestions || questionDrafts.length === 1"
                  data-cy="guidedRecording-organizer-deleteQuestion"
                  @click="openDeleteQuestion(question, index)"
                />
              </div>
              <div class="d-flex flex-wrap align-center ga-1" v-else>
                <v-btn
                  icon="mdi-arrow-up"
                  variant="text"
                  :aria-label="$t('routes.guidedRecording.moveQuestionUp', { number: index + 1 })"
                  :disabled="disabled || index === 0"
                  @click="requestReorder(question, index, 'up')"
                />
                <v-btn
                  icon="mdi-arrow-down"
                  variant="text"
                  :aria-label="$t('routes.guidedRecording.moveQuestionDown', { number: index + 1 })"
                  :disabled="disabled || index === initialQuestionDrafts.length - 1"
                  @click="requestReorder(question, index, 'down')"
                />
                <v-btn
                  icon="mdi-delete-outline"
                  variant="text"
                  color="error"
                  :aria-label="$t('routes.guidedRecording.removeQuestion', { number: index + 1 })"
                  :disabled="disabled || initialQuestionDrafts.length === 1"
                  @click="openDeleteQuestion(question, index)"
                />
              </div>
            </div>

            <v-textarea
              v-model="question.text"
              :label="$t('routes.guidedRecording.question')"
              :rules="requiredRules"
              :error-messages="questionValidationErrors[questionKey(question, index)]"
              :disabled="disabled || mutatingQuestions"
              required
              rows="3"
              auto-grow
              variant="solo-filled"
              counter="65535"
              maxlength="65535"
              :data-cy="`guidedRecording-organizer-question-${questionKey(question, index)}`"
            />
            <v-checkbox
              v-model="question.required"
              :label="$t('routes.guidedRecording.questionRequired')"
              :disabled="disabled || mutatingQuestions"
              hide-details
              class="mb-3"
            />
            <v-textarea
              v-model="question.note"
              :label="$t('routes.guidedRecording.questionNote')"
              :hint="$t('routes.guidedRecording.optional')"
              :disabled="disabled || mutatingQuestions"
              rows="2"
              auto-grow
              variant="solo-filled"
              counter="65535"
              maxlength="65535"
            />
            <v-textarea
              v-model="question.script"
              :label="$t('routes.guidedRecording.questionScript')"
              :hint="$t('routes.guidedRecording.optional')"
              :disabled="disabled || mutatingQuestions"
              rows="3"
              auto-grow
              variant="solo-filled"
              counter="65535"
              maxlength="65535"
            />
            <section
              class="guided-recording-question-duration mt-3"
              :aria-labelledby="`guided-recording-question-duration-${questionKey(question, index)}-heading`"
            >
              <h4
                :id="`guided-recording-question-duration-${questionKey(question, index)}-heading`"
                class="text-body-2 mb-1"
              >
                {{ $t('routes.guidedRecording.questionDuration') }}
              </h4>
              <v-radio-group
                :model-value="questionDurationMode(question)"
                :aria-label="$t('routes.guidedRecording.questionDuration')"
                :disabled="disabled || mutatingQuestions"
                inline
                hide-details
                @update:model-value="updateQuestionDurationMode(question, $event)"
              >
                <v-radio
                  value="unlimited"
                  :label="$t('routes.guidedRecording.questionDurationUnlimited')"
                />
                <v-radio
                  value="limited"
                  :label="$t('routes.guidedRecording.questionDurationLimited')"
                />
              </v-radio-group>
              <v-text-field
                v-if="questionDurationMode(question) === 'limited'"
                :model-value="question.maximumDurationSeconds"
                :label="$t('routes.guidedRecording.questionDurationSeconds')"
                :hint="$t('routes.guidedRecording.questionDurationRange')"
                :rules="durationRules"
                :error-messages="questionDurationValidationErrors[questionKey(question, index)]"
                :disabled="disabled || mutatingQuestions"
                type="number"
                min="5"
                max="600"
                step="1"
                required
                persistent-hint
                variant="solo-filled"
                class="mt-2"
                :data-cy="`guidedRecording-organizer-question-duration-${questionKey(question, index)}`"
                @update:model-value="updateQuestionDuration(question, $event)"
              />
            </section>
            <div v-if="hasPersistedQuestions" class="d-flex justify-end">
              <v-btn
                type="button"
                color="primary"
                variant="outlined"
                :loading="mutatingQuestions && activeQuestionId === question.id"
                :disabled="disabled || mutatingQuestions"
                :aria-label="$t('routes.guidedRecording.saveQuestion', { number: index + 1 })"
                data-cy="guidedRecording-organizer-saveQuestion"
                @click="saveQuestion(question)"
              >
                {{ $t('routes.guidedRecording.saveQuestion') }}
              </v-btn>
            </div>
          </v-card>
        </li>
      </ol>

      <v-card v-if="isAddingQuestion" variant="outlined" class="pa-4">
        <h3 ref="newQuestionHeading" class="text-subtitle-1 mb-3" tabindex="-1">
          {{ $t('routes.guidedRecording.newQuestion') }}
        </h3>
        <v-textarea
          ref="newQuestionText"
          v-model="newQuestion.text"
          :label="$t('routes.guidedRecording.question')"
          :error-messages="newQuestionError"
          :disabled="disabled || mutatingQuestions"
          required
          rows="3"
          auto-grow
          variant="solo-filled"
          counter="65535"
          maxlength="65535"
          data-cy="guidedRecording-organizer-newQuestion"
        />
        <v-checkbox
          v-model="newQuestion.required"
          :label="$t('routes.guidedRecording.questionRequired')"
          :disabled="disabled || mutatingQuestions"
          hide-details
          class="mb-3"
        />
        <v-textarea
          v-model="newQuestion.note"
          :label="$t('routes.guidedRecording.questionNote')"
          :hint="$t('routes.guidedRecording.optional')"
          :disabled="disabled || mutatingQuestions"
          rows="2"
          auto-grow
          variant="solo-filled"
          counter="65535"
          maxlength="65535"
        />
        <v-textarea
          v-model="newQuestion.script"
          :label="$t('routes.guidedRecording.questionScript')"
          :hint="$t('routes.guidedRecording.optional')"
          :disabled="disabled || mutatingQuestions"
          rows="3"
          auto-grow
          variant="solo-filled"
          counter="65535"
          maxlength="65535"
        />
        <section class="guided-recording-question-duration mt-3" aria-labelledby="guided-recording-new-question-duration-heading">
          <h4 id="guided-recording-new-question-duration-heading" class="text-body-2 mb-1">
            {{ $t('routes.guidedRecording.questionDuration') }}
          </h4>
          <v-radio-group
            :model-value="questionDurationMode(newQuestion)"
            :aria-label="$t('routes.guidedRecording.questionDuration')"
            :disabled="disabled || mutatingQuestions"
            inline
            hide-details
            @update:model-value="updateQuestionDurationMode(newQuestion, $event)"
          >
            <v-radio
              value="unlimited"
              :label="$t('routes.guidedRecording.questionDurationUnlimited')"
            />
            <v-radio
              value="limited"
              :label="$t('routes.guidedRecording.questionDurationLimited')"
            />
          </v-radio-group>
          <v-text-field
            v-if="questionDurationMode(newQuestion) === 'limited'"
            :model-value="newQuestion.maximumDurationSeconds"
            :label="$t('routes.guidedRecording.questionDurationSeconds')"
            :hint="$t('routes.guidedRecording.questionDurationRange')"
            :rules="durationRules"
            :error-messages="newQuestionDurationError"
            :disabled="disabled || mutatingQuestions"
            type="number"
            min="5"
            max="600"
            step="1"
            required
            persistent-hint
            variant="solo-filled"
            class="mt-2"
            data-cy="guidedRecording-organizer-newQuestion-duration"
            @update:model-value="updateQuestionDuration(newQuestion, $event)"
          />
        </section>
        <div class="d-flex flex-wrap justify-end ga-2">
          <v-btn type="button" variant="text" :disabled="disabled || mutatingQuestions" @click="closeAddQuestion">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            type="button"
            color="primary"
            :loading="mutatingQuestions"
            :disabled="disabled || mutatingQuestions"
            data-cy="guidedRecording-organizer-createQuestion"
            @click="createQuestion"
          >
            {{ $t('routes.guidedRecording.addQuestion') }}
          </v-btn>
        </div>
      </v-card>
    </section>

    <div class="guided-recording-actions" data-cy="guidedRecording-organizer-actions">
      <v-btn
        v-if="!isAddingQuestion"
        ref="addQuestionButton"
        type="button"
        color="primary"
        variant="outlined"
        class="mr-auto"
        :disabled="disabled || mutatingQuestions"
        data-cy="guidedRecording-organizer-addQuestion"
        @click="openAddQuestion"
      >
        <v-icon start aria-hidden="true">mdi-plus</v-icon>
        {{ $t('routes.guidedRecording.addQuestion') }}
      </v-btn>
      <v-btn
        type="submit"
        variant="outlined"
        color="primary"
        :loading="saving"
        :disabled="disabled"
        data-cy="guidedRecording-organizer-save"
      >
        {{ $t('routes.guidedRecording.save') }}
      </v-btn>
    </div>

    <v-dialog
      v-model="dialog"
      persistent
      scrollable
      :fullscreen="$vuetify.display.xs"
      :width="$vuetify.display.smAndUp ? 600 : 'auto'"
      class="vb-dialog vb-dialog-danger"
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <span>{{ $t('routes.guidedRecording.removeQuestionTitle') }}</span>
        </v-card-title>
        <v-card-text class="pt-4">
          {{ $t('routes.guidedRecording.removeQuestionDescription') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="error" :loading="mutatingQuestions" @click="confirmDeleteQuestion">
            {{ $t('routes.guidedRecording.removeQuestionConfirm') }}
          </v-btn>
          <v-btn variant="text" :disabled="mutatingQuestions" @click="cancelDeleteQuestion">
            {{ $t('common.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-form>
</template>

<script>
import SelectNodeDialog from '@/routes/console/users/components/dialogs/SelectNodeDialog.vue';

const MAXIMUM_INSTRUCTIONS_LENGTH = 4000;

const EMPTY_QUESTION = () => ({
  text: '',
  required: true,
  note: '',
  script: '',
  maximumDurationSeconds: null,
});

const cloneQuestions = (questions) => questions.map((question) => ({ ...question }));

const toInitialQuestion = (model) => ({
  text: model.questionText || '',
  required: model.questionRequired !== false,
  note: model.questionNote || '',
  script: model.questionScript || '',
  maximumDurationSeconds: model.questionMaximumDurationSeconds
    ?? model.maximumDurationSeconds
    ?? model.maximumdurationseconds
    ?? null,
});

export default {
  name: 'GuidedRecordingForm',
  components: { SelectNodeDialog },
  props: {
    modelValue: { type: Object, required: true },
    questions: { type: Array, required: true },
    fieldErrors: { type: Object, required: true },
    formError: { type: String, default: '' },
    destinationError: { type: String, default: '' },
    questionError: { type: String, default: '' },
    announcement: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    mutatingQuestions: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'save', 'clear-field-error', 'add-question', 'update-question', 'delete-question', 'reorder-questions'],
  data() {
    return {
      activeQuestionId: '',
      destinationRequired: false,
      dialog: false,
      initialQuestionDrafts: [toInitialQuestion(this.modelValue)],
      isAddingQuestion: false,
      newQuestion: EMPTY_QUESTION(),
      newQuestionError: '',
      pendingDeleteQuestionId: '',
      pendingDeleteQuestionIndex: -1,
      pendingFocusQuestionId: '',
      questionDrafts: cloneQuestions(this.questions),
      questionRefs: Object.create(null),
      questionValidationErrors: {},
      questionDurationValidationErrors: {},
      newQuestionDurationError: '',
      questionsHeadingId: 'guided-recording-questions-heading',
      maximumInstructionsLength: MAXIMUM_INSTRUCTIONS_LENGTH,
      instructionsRules: [
        value => (value == null || value.length <= MAXIMUM_INSTRUCTIONS_LENGTH)
          || this.$t('routes.guidedRecording.instructionsTooLong', { maximum: MAXIMUM_INSTRUCTIONS_LENGTH }),
      ],
      requiredRules: [value => Boolean(value && value.trim()) || this.$t('routes.guidedRecording.required')],
      durationRules: [value => this.isValidDuration(value) || this.$t('routes.guidedRecording.questionDurationInvalid')],
      expiryRules: [value => this.isValidExpiry(value) || this.$t('routes.guidedRecording.expiryInvalid')],
    };
  },
  watch: {
    questions: {
      handler(questions) {
        this.questionDrafts = cloneQuestions(questions);
        this.restoreQuestionFocus();
      },
      deep: false,
    },
    questionError(error) {
      if (error && this.pendingFocusQuestionId === 'new-question') {
        this.pendingFocusQuestionId = '';
        this.focusAddQuestionButton();
        return;
      }

      if (error && !this.pendingFocusQuestionId) {
        this.focusAddQuestionButton();
      }
    },
  },
  methods: {
    updateField(field, value) {
      this.$emit('clear-field-error', field);
      this.$emit('update:modelValue', { ...this.modelValue, [field]: value });
    },
    toDateOnly(value) {
      if (value === null || value === undefined || value === '') {
        return null;
      }

      if (value instanceof Date) {
        return [
          value.getFullYear(),
          String(value.getMonth() + 1).padStart(2, '0'),
          String(value.getDate()).padStart(2, '0'),
        ].join('-');
      }

      return typeof value === 'string' ? value.slice(0, 10) : '';
    },
    updateDestination(destinationNodeId) {
      this.destinationRequired = false;
      this.updateField('destinationNodeId', destinationNodeId);
    },
    async validate() {
      this.destinationRequired = !this.modelValue.destinationNodeId;
      if (this.destinationRequired) {
        await this.$nextTick();
        this.$refs.destinationButton?.$el?.focus();
        return false;
      }

      const result = await this.$refs.form.validate();
      return result.valid;
    },
    async submit() {
      if (await this.validate()) {
        this.$emit('save', { questions: cloneQuestions(this.initialQuestionDrafts) });
      }
    },
    openAddQuestion() {
      this.isAddingQuestion = true;
      this.newQuestion = EMPTY_QUESTION();
      this.newQuestionError = '';
      this.newQuestionDurationError = '';
      this.$nextTick(() => this.$refs.newQuestionText?.focus());
    },
    closeAddQuestion() {
      this.isAddingQuestion = false;
      this.newQuestionError = '';
      this.newQuestionDurationError = '';
      this.focusAddQuestionButton();
    },
    createQuestion() {
      if (!this.newQuestion.text.trim()) {
        this.newQuestionError = this.$t('routes.guidedRecording.required');
      } else {
        this.newQuestionError = '';
      }

      this.newQuestionDurationError = this.getDurationValidationError(
        this.newQuestion.maximumDurationSeconds,
      );
      if (this.newQuestionError || this.newQuestionDurationError) {
        return;
      }

      this.pendingFocusQuestionId = 'new-question';
      if (!this.hasPersistedQuestions) {
        this.initialQuestionDrafts = [...this.initialQuestionDrafts, { ...this.newQuestion }];
        this.isAddingQuestion = false;
        this.newQuestionError = '';
        this.$nextTick(() => this.questionRefs[`initial-${this.initialQuestionDrafts.length - 1}`]?.focus());
        return;
      }

      this.$emit('add-question', { ...this.newQuestion });
      this.isAddingQuestion = false;
      this.newQuestionError = '';
    },
    saveQuestion(question) {
      const textError = question.text.trim() ? '' : this.$t('routes.guidedRecording.required');
      this.questionValidationErrors = { ...this.questionValidationErrors, [question.id]: textError };

      const durationError = this.getDurationValidationError(question.maximumDurationSeconds);
      this.questionDurationValidationErrors = {
        ...this.questionDurationValidationErrors,
        [question.id]: durationError,
      };
      if (textError || durationError) {
        return;
      }

      this.activeQuestionId = question.id;
      this.pendingFocusQuestionId = question.id;
      this.$emit('update-question', {
        questionId: question.id,
        changes: { ...question },
      });
    },
    questionDurationMode(question) {
      return question.maximumDurationSeconds === null || question.maximumDurationSeconds === undefined
        ? 'unlimited'
        : 'limited';
    },
    updateQuestionDurationMode(question, mode) {
      if (mode === 'unlimited') {
        question.maximumDurationSeconds = null;
      } else if (!this.isValidDuration(question.maximumDurationSeconds)
        || question.maximumDurationSeconds === null
        || question.maximumDurationSeconds === undefined) {
        question.maximumDurationSeconds = 60;
      }
      const durationError = this.getDurationValidationError(question.maximumDurationSeconds);
      if (question === this.newQuestion) {
        this.newQuestionDurationError = durationError;
        return;
      }

      const questionId = question.id || '';
      this.questionDurationValidationErrors = {
        ...this.questionDurationValidationErrors,
        [questionId]: durationError,
      };
    },
    updateQuestionDuration(question, value) {
      const numericValue = value === '' ? value : Number(value);
      question.maximumDurationSeconds = Number.isNaN(numericValue) ? value : numericValue;
      const durationError = this.getDurationValidationError(question.maximumDurationSeconds);
      if (question === this.newQuestion) {
        this.newQuestionDurationError = durationError;
        return;
      }

      const questionId = question.id || '';
      this.questionDurationValidationErrors = {
        ...this.questionDurationValidationErrors,
        [questionId]: durationError,
      };
    },
    isValidDuration(value) {
      if (value === null || value === undefined) {
        return true;
      }

      const duration = typeof value === 'number' ? value : Number(value);
      return Number.isInteger(duration) && duration >= 5 && duration <= 600;
    },
    getDurationValidationError(value) {
      return this.isValidDuration(value) ? '' : this.$t('routes.guidedRecording.questionDurationInvalid');
    },
    isValidExpiry(value) {
      if (value === null || value === undefined || value === '') {
        return true;
      }

      const dateOnly = this.toDateOnly(value);
      const expiryDate = new Date(`${dateOnly}T00:00:00.000Z`);
      const isCalendarDate = /^\d{4}-\d{2}-\d{2}$/.test(dateOnly)
        && !Number.isNaN(expiryDate.getTime())
        && dateOnly === [
          expiryDate.getUTCFullYear(),
          String(expiryDate.getUTCMonth() + 1).padStart(2, '0'),
          String(expiryDate.getUTCDate()).padStart(2, '0'),
        ].join('-');
      return isCalendarDate;
    },
    requestReorder(question, index, direction) {
      if (!this.hasPersistedQuestions) {
        const nextIndex = direction === 'up' ? index - 1 : index + 1;
        if (nextIndex < 0 || nextIndex >= this.initialQuestionDrafts.length) {
          return;
        }

        const reordered = [...this.initialQuestionDrafts];
        [reordered[index], reordered[nextIndex]] = [reordered[nextIndex], reordered[index]];
        this.initialQuestionDrafts = reordered;
        this.$nextTick(() => this.questionRefs[`initial-${nextIndex}`]?.focus());
        return;
      }

      const questionId = question.id;
      this.activeQuestionId = questionId;
      this.pendingFocusQuestionId = questionId;
      this.$emit('reorder-questions', { questionId, direction });
    },
    openDeleteQuestion(question, index) {
      if (this.displayQuestions.length <= 1) {
        return;
      }

      this.pendingDeleteQuestionId = question.id || '';
      this.pendingDeleteQuestionIndex = index;
      this.dialog = true;
    },
    cancelDeleteQuestion() {
      const questionId = this.hasPersistedQuestions
        ? this.pendingDeleteQuestionId
        : `initial-${this.pendingDeleteQuestionIndex}`;
      this.dialog = false;
      this.pendingDeleteQuestionId = '';
      this.pendingDeleteQuestionIndex = -1;
      this.pendingFocusQuestionId = questionId;
      this.restoreQuestionFocus();
    },
    confirmDeleteQuestion() {
      if (!this.hasPersistedQuestions) {
        const deletedIndex = this.pendingDeleteQuestionIndex;
        this.initialQuestionDrafts = this.initialQuestionDrafts.filter((_, index) => index !== deletedIndex);
        this.pendingDeleteQuestionId = '';
        this.pendingDeleteQuestionIndex = -1;
        this.dialog = false;
        const focusIndex = Math.min(deletedIndex, this.initialQuestionDrafts.length - 1);
        this.$nextTick(() => this.questionRefs[`initial-${focusIndex}`]?.focus());
        return;
      }

      const questionIndex = this.questionDrafts.findIndex(
        (question) => question.id === this.pendingDeleteQuestionId,
      );
      const nextQuestion = this.questionDrafts[questionIndex + 1] || this.questionDrafts[questionIndex - 1];
      this.pendingFocusQuestionId = nextQuestion?.id || '';
      this.activeQuestionId = this.pendingDeleteQuestionId;
      this.$emit('delete-question', this.pendingDeleteQuestionId);
      this.pendingDeleteQuestionId = '';
      this.dialog = false;
    },
    setQuestionRef(questionId, element) {
      if (element) {
        this.questionRefs[questionId] = element;
        return;
      }

      delete this.questionRefs[questionId];
    },
    questionKey(question, index) {
      return question.id || `initial-${index}`;
    },
    focusAddQuestionButton() {
      this.$nextTick(() => {
        const button = this.$refs.addQuestionButton;
        (button?.$el || button)?.focus?.();
      });
    },
    async restoreQuestionFocus() {
      if (this.pendingFocusQuestionId === 'new-question') {
        const addedQuestion = this.questions[this.questions.length - 1];
        this.pendingFocusQuestionId = addedQuestion?.id || '';
      }
      const questionId = this.pendingFocusQuestionId;
      this.pendingFocusQuestionId = '';
      this.activeQuestionId = '';
      if (!questionId) {
        return;
      }

      await this.$nextTick();
      this.questionRefs[questionId]?.focus();
    },
  },
  computed: {
    displayQuestions() {
      return this.hasPersistedQuestions ? this.questionDrafts : this.initialQuestionDrafts;
    },
    displayDestinationError() {
      if (this.destinationError) {
        return this.destinationError;
      }

      return this.destinationRequired ? this.$t('routes.guidedRecording.required') : '';
    },
    hasPersistedQuestions() {
      return this.questions.length > 0;
    },
    expiryStatus() {
      return this.modelValue.expiresAt
        ? this.$t('routes.guidedRecording.expiryConfiguredStatus')
        : this.$t('routes.guidedRecording.expiryNotConfiguredStatus');
    },
  },
};
</script>

<style lang="scss" scoped>
@layer app {
  .guided-recording-question-list {
    list-style: none;
  }

  .guided-recording-question-card h3:focus-visible,
  .guided-recording-question h3:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 4px;
  }

  .guided-recording-actions {
    align-items: center;
    background: rgb(var(--v-theme-surface));
    bottom: 0;
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    padding-top: 20px;
    position: sticky;
  }

  .guided-recording-actions > .v-btn {
    flex: 0 0 auto;
    white-space: nowrap;
  }

  @media (max-width: 600px) {
    .guided-recording-actions {
      flex-direction: column-reverse;
    }

    .guided-recording-actions > .v-btn {
      width: 100%;
    }
  }
}
</style>
